import { useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import type { AxisZoomPercentages } from '../GeneratedAxisChart/hooks/useGeneratedAxisChart.types';
import type { SelectBoxProps } from './SelectBox.types';
import './css/select-box.css';

type Point = { x: number; y: number };
type Drag = { start: Point; end: Point; pointer: number };

/** Draws percentage bounds; the chart owner independently inverts each axis. */
export const SelectBox = ({ data_area = 'center', axes = {}, handleZoom, zoom_y, zoom_x }: SelectBoxProps) => {
  const container = useRef<HTMLDivElement>(null);
  const held = useRef<Drag | null>(null);
  const [selection, setSelection] = useState<Drag | null>(null);
  if (!zoom_x && !zoom_y) return null;
  const point = (event: PointerEvent<HTMLDivElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clamp = (value: number) => Math.max(0, Math.min(100, value));
    return { x: clamp((event.clientX - rect.left) / rect.width * 100), y: clamp((event.clientY - rect.top) / rect.height * 100) };
  };
  const cancel = () => { held.current = null; setSelection(null); };
  const emit = (start: Point, end: Point) => {
    const percentages: AxisZoomPercentages = {};
    for (const location of ['left', 'right', 'top', 'bottom'] as const) {
      const vertical = location === 'left' || location === 'right';
      if (vertical ? !zoom_y : !zoom_x) continue;
      if (!axes[location]?.scale) continue;
      const values = vertical ? [start.y, end.y] : [start.x, end.x];
      percentages[location] = [Math.min(...values), Math.max(...values)];
    }
    handleZoom?.(percentages, 'zoom');
  };
  const select_box = <div
    ref={container}
    data-area={data_area}
    className='select-box'
    role='group'
    tabIndex={0}
    aria-label='Drag to zoom. Press plus to zoom in, or Escape to reset.'
    onPointerDown={event => {
      if (event.button !== 0 || held.current) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      event.preventDefault();
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);
      const start = point(event);
      held.current = { start, end: start, pointer: event.pointerId };
      setSelection(held.current);
    }}
    onPointerMove={event => {
      if (held.current?.pointer !== event.pointerId) return;
      held.current = { ...held.current, end: point(event) };
      setSelection(held.current);
    }}
    onPointerUp={event => {
      const drag = held.current;
      if (!drag || drag.pointer !== event.pointerId) return;
      const end = point(event);
      const rect = event.currentTarget.getBoundingClientRect();
      const valid = (!zoom_x || Math.abs(end.x - drag.start.x) * rect.width / 100 >= 3)
        && (!zoom_y || Math.abs(end.y - drag.start.y) * rect.height / 100 >= 3);
      cancel();
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      if (valid) emit(drag.start, end);
    }}
    onPointerCancel={cancel}
    onLostPointerCapture={cancel}
    onKeyDown={event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (held.current) cancel(); else handleZoom?.(undefined, 'reset');
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault(); emit({ x: 25, y: 25 }, { x: 75, y: 75 });
      }
    }}
    onDoubleClick={() => { cancel(); handleZoom?.(undefined, 'reset'); }}
  >
    {selection && <div className='select-box-selection' style={{
      left: `${zoom_x ? Math.min(selection.start.x, selection.end.x) : 0}%`,
      top: `${zoom_y ? Math.min(selection.start.y, selection.end.y) : 0}%`,
      width: `${zoom_x ? Math.abs(selection.end.x - selection.start.x) : 100}%`,
      height: `${zoom_y ? Math.abs(selection.end.y - selection.start.y) : 100}%`,
    }} />}
  </div>;
  return select_box;
};
